import { useCallback, useEffect, useRef, useState } from "react";
import { Paperclip, Upload, Trash2, FileText, Loader2, Download } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, openSigned } from "@/lib/api";
import { Button } from "@/components/ui/button";

export function MeetingAttachments({ meetingId, canManage }) {
  const [list, setList] = useState(null);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(
    () => api.get(`/meetings/${meetingId}/attachments`).then((r) => setList((r.data || []).filter((d) => !d.agenda_item_id))).catch(() => setList([])),
    [meetingId]
  );
  useEffect(() => { load(); }, [load]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/meetings/${meetingId}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Vedlegg lagt til – følger innkallingen automatisk");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/documents/${id}`);
      toast.success("Vedlegg fjernet");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="rounded-xl border border-border/60 bg-card shadow-sm" data-testid="meeting-attachments">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/60 px-6 py-4">
        <div>
          <h3 className="flex items-center gap-1.5 font-headings text-sm font-bold uppercase tracking-wider text-muted-foreground">
            <Paperclip className="h-4 w-4 text-primary" /> Vedlegg til innkalling
          </h3>
          <p className="mt-1 text-xs text-muted-foreground">Årsregnskap, årsberetning og andre dokumenter som følger innkallingen automatisk.</p>
        </div>
        {canManage ? (
          <>
            <input ref={inputRef} type="file" className="hidden" onChange={onFile} data-testid="attachment-file-input" />
            <Button size="sm" className="h-9 gap-2" data-testid="attachment-upload-btn" disabled={uploading} onClick={() => inputRef.current?.click()}>
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Last opp
            </Button>
          </>
        ) : null}
      </div>
      {list === null ? (
        <p className="p-8 text-center text-sm text-muted-foreground">Laster …</p>
      ) : list.length === 0 ? (
        <div className="flex flex-col items-center gap-2 p-10 text-center text-sm text-muted-foreground" data-testid="attachments-empty">
          <FileText className="h-8 w-8 text-muted-foreground/40" />
          Ingen vedlegg ennå. Last opp årsregnskap/årsberetning som skal følge innkallingen.
        </div>
      ) : (
        <div className="divide-y divide-border/60">
          {list.map((d) => (
            <div key={d.id} className="flex items-center gap-3 px-6 py-3.5" data-testid={`attachment-${d.id}`}>
              <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary"><FileText className="h-4 w-4" /></div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{d.name}</p>
                <p className="text-xs text-muted-foreground">{d.category} · lastet opp av {d.uploaded_by_name}</p>
              </div>
              <button className="grid h-8 w-8 place-items-center rounded-md text-muted-foreground transition-colors hover:text-primary" title="Last ned" data-testid={`attachment-download-${d.id}`} onClick={() => openSigned(`/documents/${d.id}/download-link`).catch(() => toast.error("Kunne ikke åpne dokumentet"))}>
                <Download className="h-4 w-4" />
              </button>
              {canManage ? (
                <button className="grid h-8 w-8 place-items-center rounded-md text-muted-foreground transition-colors hover:text-destructive" title="Fjern" data-testid={`attachment-delete-${d.id}`} onClick={() => remove(d.id)}>
                  <Trash2 className="h-4 w-4" />
                </button>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
